import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { CREW_KEY, CREW_RESOURCE, type Assignment } from '../crew/types';
import { eventDays } from '../events/calendar';
import { warsawParts, type EventView } from '../events/labels';
import { SettingsDialog } from '../settings/SettingsDialog';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import './schedule.css';
import { StageTypesSettings, useStageTypes } from './StageTypesSettings';
import { TasksPanel } from './TasksPanel';
import { SCHEDULE_KEY, STAGE_TYPES_RESOURCE, stageInstants, stageTimeText, STAGES_RESOURCE, TASKS_RESOURCE, type Stage } from './types';
import { NotesInput } from '../ui/NotesInput';
import { leaveGuarded, sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

type PersonOption = { id: string; name: string };

/** The people of the event's crew (each once, without those who declined): who a stage or a task can be for. */
export function crewPeople(list: Assignment[]): PersonOption[] {
  const seen = new Map<string, string>();
  for (const a of list) if (a.personId && a.personName && a.status !== 'odmowa' && !seen.has(a.personId)) seen.set(a.personId, a.personName);
  return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
}

function errorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Nie udało się zapisać';
  return fieldIssues(error).map((i) => i.message).join(' ') || error.message;
}

/** "Harmonogram" tab of the event card: the stages of the event (who loads, when) and its tasks. */
export function ScheduleTab({ event }: { event: EventView }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const seeStages = !!me && can(me, STAGES_RESOURCE, 'view');
  const key = [...SCHEDULE_KEY, 'stages', event.id];
  const stages = useQuery({ queryKey: key, enabled: seeStages, queryFn: () => api<{ stages: Stage[] }>(`/api/schedule/events/${event.id}/stages`) });
  const crew = useQuery({ queryKey: [...CREW_KEY, 'event', event.id], enabled: !!me && can(me, CREW_RESOURCE, 'view'), queryFn: () => api<{ assignments: Assignment[] }>(`/api/crew/events/${event.id}/assignments`) });
  const [form, setForm] = useState<Stage | 'new' | null>(null);
  const [settings, setSettings] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: SCHEDULE_KEY });
  const remove = useMutation({ mutationFn: (s: Stage) => api(`/api/schedule/stages/${s.id}?version=${s.version}`, { method: 'DELETE' }), onSettled: refresh });

  if (!me) return null;
  const people = crewPeople(crew.data?.assignments ?? []);
  const list = stages.data?.stages ?? [];
  const mayAdd = can(me, STAGES_RESOURCE, 'create') && fieldVisible(me, STAGES_RESOURCE, 'stageTypeId') && fieldVisible(me, STAGES_RESOURCE, 'startsAt');
  const mayEdit = can(me, STAGES_RESOURCE, 'edit');
  const mayDelete = can(me, STAGES_RESOURCE, 'delete');
  const maySettings = ['create', 'edit', 'delete'].some((a) => can(me, STAGE_TYPES_RESOURCE, a));
  const removeError = remove.error instanceof ApiError ? remove.error : null;

  return (
    <div role="tabpanel" aria-label="Harmonogram" className="schedule-tab">
      {seeStages && (
        <section className="schedule-section" aria-label="Etapy" data-feature-id="schedule.stages">
          <div className="schedule-head">
            <h3>Etapy</h3>
            <span className="schedule-head-actions">
              {mayAdd && form !== 'new' && (
                <button className="btn small ghost" type="button" onClick={() => leaveGuarded(() => setForm('new'))}>
                  + Dodaj etap
                </button>
              )}
              {maySettings && <RowMenu icon="gear" label="Ustawienia: rodzaje etapów" featureId="schedule.stage_types" actions={[{ label: 'Rodzaje etapów…', onSelect: () => setSettings(true) }]} />}
            </span>
          </div>
          {stages.isLoading && <p className="muted small" role="status">Wczytywanie…</p>}
          {stages.error && <p className="alert" role="alert">{stages.error instanceof ApiError ? stages.error.message : 'Nie udało się wczytać harmonogramu'}</p>}
          {stages.data && list.length === 0 && form !== 'new' && <p className="muted small">Brak etapów. Dodaj np. załadunek, wyjazd, montaż i próbę — z godziną i osobą odpowiedzialną.</p>}
          {list.length > 0 && (
            <ul className="stage-list" aria-label="Etapy wydarzenia">
              {list.map((s) =>
                form !== 'new' && form?.id === s.id ? (
                  <li key={s.id}>
                    <StageForm me={me} event={event} base={s} people={people} onDone={() => setForm(null)} />
                  </li>
                ) : (
                  <li key={s.id} className="stage-row">
                    <span className="stage-time">{s.startsAt ? stageTimeText(s.startsAt, s.endsAt) : '—'}</span>
                    <span className="stage-main">
                      <span className="stage-name">
                        {s.stageTypeName ?? 'Etap'}
                        {s.stageTypeDeleted && <span className="muted"> (rodzaj usunięty)</span>}
                        {s.automatic && (
                          <span className="chip stage-auto" title="Godzina liczy się sama z czasu dojazdu trasy. Zmieniona ręcznie zostaje taka, jak ją ustawisz." data-feature-id="schedule.departure">
                            ustawiony sam
                          </span>
                        )}
                      </span>
                      {(s.personName || s.notes) && (
                        <span className="task-meta">
                          {s.personName && <span>{s.personName}</span>}
                          {s.notes && <span>{s.notes}</span>}
                        </span>
                      )}
                    </span>
                    {(mayEdit || mayDelete) && (
                      <RowMenu
                        label={`Więcej działań: ${s.stageTypeName ?? 'etap'}`}
                        actions={[...(mayEdit ? [{ label: 'Edytuj', onSelect: () => leaveGuarded(() => setForm(s)) }] : []), ...(mayDelete ? [{ label: 'Usuń', danger: true, onSelect: () => remove.mutate(s) }] : [])]}
                      />
                    )}
                  </li>
                ),
              )}
            </ul>
          )}
          {removeError?.status === 409 && (
            <ConflictNotice
              onReload={() => {
                remove.reset();
                void refresh();
              }}
            />
          )}
          {removeError && removeError.status !== 409 && <p className="alert" role="alert">{removeError.message}</p>}
          {form === 'new' && <StageForm me={me} event={event} base={null} people={people} onDone={() => setForm(null)} />}
        </section>
      )}
      {can(me, TASKS_RESOURCE, 'view') && <TasksPanel eventId={event.id} crew={people} />}
      {settings && (
        <SettingsDialog title="Rodzaje etapów" onClose={() => setSettings(false)}>
          <StageTypesSettings me={me} heading={false} />
        </SettingsDialog>
      )}
    </div>
  );
}

/** Add or edit a stage: kind, day and hours (end optional), the person responsible (from the crew), notes. */
function StageForm({ me, event, base, people, onDone }: { me: Me; event: EventView; base: Stage | null; people: PersonOption[]; onDone: () => void }) {
  const queryClient = useQueryClient();
  const types = useStageTypes();
  const firstDay = event.startsAt && event.endsAt ? eventDays({ startsAt: event.startsAt, endsAt: event.endsAt }).first : '';
  const start = base?.startsAt ? warsawParts(base.startsAt) : { day: firstDay, time: '' };
  const [stageTypeId, setStageTypeId] = useState(base?.stageTypeId ?? '');
  const [day, setDay] = useState(start.day);
  const [from, setFrom] = useState(start.time);
  const [to, setTo] = useState(base?.endsAt ? warsawParts(base.endsAt).time : '');
  const [personId, setPersonId] = useState(base?.personId ?? '');
  const [notes, setNotes] = useState(base?.notes ?? '');
  const show = (field: string) => fieldVisible(me, STAGES_RESOURCE, field);
  const typeOptions = types.data?.stageTypes ?? [];
  const keepDeleted = base?.stageTypeId && !typeOptions.some((t) => t.id === base.stageTypeId);
  const choices = base?.personId && !people.some((p) => p.id === base.personId) ? [...people, { id: base.personId, name: base.personName ?? 'Osoba' }] : people;

  const save = useMutation({
    mutationFn: () => {
      const { startsAt, endsAt } = stageInstants(day, from, to);
      const all: Record<string, unknown> = { stageTypeId, startsAt, endsAt, personId: personId || null, notes: notes.trim() };
      const same = (k: string, v: unknown) => {
        const was = (base as Record<string, unknown> | null)?.[k];
        return typeof v === 'string' && typeof was === 'string' && (k === 'startsAt' || k === 'endsAt') ? new Date(v).getTime() === new Date(was).getTime() : was === v;
      };
      // rule 7a: only visible fields; when editing only the changed ones
      const body = Object.fromEntries(Object.entries(all).filter(([k, v]) => show(k) && (!base || !same(k, v)) && !(k === 'notes' && !base && v === '')));
      return base ? api<Stage>(`/api/schedule/stages/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }) : api<Stage>(`/api/schedule/events/${event.id}/stages`, { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SCHEDULE_KEY });
      onDone();
    },
  });
  const ready = stageTypeId !== '' && day !== '' && from !== '';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate();
  };
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const now = { stageTypeId, day, from, to, personId, notes };
  const opening = useOpening(now);
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(now, opening),
    save: async () => {
      if (!ready) return false;
      await save.mutateAsync();
      return true;
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <form ref={guardRef} className="schedule-form" aria-label={base ? `Edycja etapu: ${base.stageTypeName ?? ''}` : 'Nowy etap'} onSubmit={submit}>
      <div className="field-row">
        <label className="field">
          <span>Etap</span>
          <select autoFocus value={stageTypeId} onChange={(e) => setStageTypeId(e.target.value)} required>
            <option value="">— wybierz —</option>
            {typeOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            {keepDeleted && <option value={base.stageTypeId}>{base.stageTypeName} (usunięty)</option>}
          </select>
        </label>
        <label className="field">
          <span>Dzień</span>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
        </label>
        <label className="field">
          <span>Od</span>
          <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} required />
        </label>
        <label className="field">
          <span>Do (opcjonalnie)</span>
          <input type="time" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      <div className="field-row">
        {show('personId') && (
          <label className="field">
            <span>Kto</span>
            <select value={personId} onChange={(e) => setPersonId(e.target.value)}>
              <option value="">— nikt —</option>
              {choices.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {show('notes') && (
          <label className="field grow">
            <span>Uwagi</span>
            <NotesInput value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="np. wjazd od zaplecza" maxLength={2000} />
          </label>
        )}
      </div>
      {show('personId') && people.length === 0 && <p className="hint">Osobę wybierasz z obsady wydarzenia — najpierw dodaj ją w zakładce „Obsada”.</p>}
      {error?.status === 409 && <ConflictNotice onReload={onDone} />}
      {error && error.status !== 409 && (
        <p className="alert" role="alert">
          {errorText(error)}
        </p>
      )}
      <div className="actions">
        <button className="btn small primary" type="submit" disabled={!ready || save.isPending}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz' : 'Dodaj etap'}
        </button>
        <button className="btn small" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
